import React, { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import {
  QrCode, RefreshCw, Trash2, Printer,
  Users, CheckCircle, Search
} from 'lucide-react';

const roleLabels = {
  admin: 'Administrator', manager: 'Manager', section_chief: 'Sef Sectie',
  technician: 'Tehnician', operator: 'Operator', procurement: 'Achizitii',
  viewer: 'Vizitator', auditor: 'Auditor'
};

const BadgeManagementPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [generating, setGenerating] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [printing, setPrinting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/badges/list');
      setUsers(res.data);
    } catch (err) {
      toast.error('Eroare la incarcarea datelor');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleGenerate = async (userId) => {
    setGenerating(userId);
    try {
      await api.post(`/badges/generate/${userId}`);
      toast.success('Ecuson generat cu succes');
      fetchData();
    } catch (err) {
      toast.error('Eroare la generarea ecusonului');
    } finally {
      setGenerating(null);
    }
  };

  const handleRevoke = async (userId) => {
    if (!window.confirm('Sigur doriti sa revocati ecusonul?')) return;
    try {
      await api.post(`/badges/revoke/${userId}`);
      toast.success('Ecuson revocat');
      fetchData();
    } catch (err) {
      toast.error('Eroare la revocare');
    }
  };

  const handleBulkGenerate = async () => {
    if (!window.confirm('Generati ecusoane pentru toti utilizatorii fara ecuson?')) return;
    try {
      const res = await api.post('/badges/generate-bulk');
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error('Eroare la generare bulk');
    }
  };

  const handleRegenerateAll = async () => {
    if (!window.confirm('ATENTIE: Se vor regenera TOATE ecusoanele. Cele vechi vor fi invalide. Continuati?')) return;
    try {
      const res = await api.post('/badges/regenerate-all');
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error('Eroare la regenerare');
    }
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === filtered.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map(u => u.id)));
    }
  };

  const handlePrintSelected = async () => {
    if (selected.size === 0) {
      toast.error('Selectati cel putin un utilizator');
      return;
    }
    setPrinting(true);
    try {
      const res = await api.post('/badges/print-prepare', { user_ids: Array.from(selected) });
      const { badges, generated_count } = res.data;
      if (generated_count > 0) {
        toast.success(`Ecusoane generate automat pentru ${generated_count} utilizatori`);
        fetchData();
      }
      openPrintWindow(badges);
    } catch (err) {
      toast.error('Eroare la pregatirea ecusoanelor');
    } finally {
      setPrinting(false);
    }
  };

  const openPrintWindow = (badges) => {
    const pw = window.open('', '_blank');
    if (!pw) {
      toast.error('Permiteti popup-uri pentru a printa ecusoanele');
      return;
    }

    const BADGES_PER_PAGE = 3;
    const pages = [];
    for (let i = 0; i < badges.length; i += BADGES_PER_PAGE) {
      pages.push(badges.slice(i, i + BADGES_PER_PAGE));
    }

    const badgeHTML = (b) => {
      const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(b.badge_token)}`;
      const role = roleLabels[b.role] || b.role;
      return `
        <div class="badge-pair">
          <div class="badge-side fata">
            <div class="badge-header">
              <div class="badge-title">MAINTIX</div>
              <div class="badge-subtitle">Smart Maintenance &amp; Asset Control</div>
            </div>
            <div class="badge-body">
              <div class="badge-name">${b.full_name}</div>
              <div class="badge-role">${role}</div>
              <div class="qr-wrap"><img src="${qrUrl}" class="qr-img" /></div>
            </div>
            <div class="badge-footer">Scanati codul QR pentru autentificare automata</div>
          </div>
          <div class="fold-line"><span>INDOIRE PE LINIA CENTRALA</span></div>
          <div class="badge-side verso">
            <div class="badge-footer-v">Scanati codul QR pentru autentificare automata</div>
            <div class="badge-body">
              <div class="qr-wrap"><img src="${qrUrl}" class="qr-img" /></div>
              <div class="badge-role">${role}</div>
              <div class="badge-name">${b.full_name}</div>
            </div>
            <div class="badge-header">
              <div class="badge-subtitle">Smart Maintenance &amp; Asset Control</div>
              <div class="badge-title">MAINTIX</div>
            </div>
          </div>
        </div>`;
    };

    const pagesHTML = pages.map((pageBadges, pi) => `
      <div class="page ${pi > 0 ? 'page-break' : ''}">
        <div class="badges-row">
          ${pageBadges.map(b => badgeHTML(b)).join('')}
        </div>
      </div>
    `).join('');

    pw.document.write(`<!DOCTYPE html><html><head><title>Ecusoane MAINTIX</title>
<style>
  @page { size: A4 portrait; margin: 8mm; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Segoe UI', Arial, sans-serif; background: #e2e8f0; }
  .page { width: 210mm; min-height: 297mm; margin: 0 auto; background: white; padding: 8mm; }
  .page-break { page-break-before: always; }
  .badges-row { display: flex; gap: 4mm; justify-content: flex-start; align-items: flex-start; }

  .badge-pair {
    width: 56mm;
    border: 0.3mm dashed #94a3b8;
    border-radius: 1mm;
    overflow: hidden;
    flex-shrink: 0;
  }

  .badge-side {
    width: 56mm;
    height: 91mm;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .verso {
    transform: rotate(180deg);
  }

  .badge-header {
    background: linear-gradient(135deg, #0052CC, #003d99);
    padding: 3mm 2mm;
    text-align: center;
    flex-shrink: 0;
  }
  .badge-title {
    color: white;
    font-size: 12pt;
    font-weight: 800;
    letter-spacing: 1.5px;
    line-height: 1.2;
  }
  .badge-subtitle {
    color: rgba(255,255,255,0.8);
    font-size: 5pt;
    margin-top: 0.5mm;
  }

  .badge-body {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2mm 2mm 1mm;
  }

  .badge-name {
    font-size: 8pt;
    font-weight: 700;
    color: #1e293b;
    text-align: center;
    line-height: 1.2;
    margin-bottom: 1mm;
  }
  .badge-role {
    font-size: 5.5pt;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    background: #f1f5f9;
    padding: 0.5mm 2mm;
    border-radius: 2mm;
    margin-bottom: 2mm;
  }

  .qr-wrap {
    width: 28mm;
    height: 28mm;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f8fafc;
    border-radius: 1.5mm;
    padding: 1mm;
  }
  .qr-img {
    width: 26mm;
    height: 26mm;
    image-rendering: pixelated;
  }

  .badge-footer, .badge-footer-v {
    text-align: center;
    padding: 1.5mm 1mm;
    font-size: 4.5pt;
    color: #94a3b8;
    background: #f8fafc;
    flex-shrink: 0;
  }

  .fold-line {
    border-top: 0.3mm dashed #cbd5e1;
    border-bottom: 0.3mm dashed #cbd5e1;
    text-align: center;
    padding: 0.8mm 0;
    background: #f8fafc;
  }
  .fold-line span {
    font-size: 4pt;
    color: #94a3b8;
    letter-spacing: 1px;
    text-transform: uppercase;
  }

  @media print {
    body { background: white; }
    .page { margin: 0; padding: 8mm; box-shadow: none; }
    .no-print { display: none !important; }
  }
  @media screen {
    .page { margin: 10mm auto; box-shadow: 0 2px 10px rgba(0,0,0,0.15); }
  }

  .toolbar {
    position: sticky;
    top: 0;
    z-index: 100;
    background: #0052CC;
    color: white;
    padding: 10px 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }
  .toolbar h2 { font-size: 16px; font-weight: 600; }
  .toolbar button {
    background: white;
    color: #0052CC;
    border: none;
    padding: 8px 20px;
    border-radius: 6px;
    font-weight: 600;
    cursor: pointer;
    font-size: 14px;
  }
  .toolbar button:hover { background: #e0e7ff; }
</style></head><body>
<div class="toolbar no-print">
  <h2>Ecusoane MAINTIX - ${badges.length} ecuson${badges.length > 1 ? 'e' : ''}</h2>
  <button onclick="window.print()">Printeaza</button>
</div>
${pagesHTML}
</body></html>`);
    pw.document.close();
  };

  const filtered = users.filter(u => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (u.full_name || '').toLowerCase().includes(s) ||
           (u.email || '').toLowerCase().includes(s) ||
           (u.role || '').toLowerCase().includes(s);
  });

  const withBadge = users.filter(u => u.has_badge).length;
  const allSelected = filtered.length > 0 && selected.size === filtered.length;

  return (
    <div className="space-y-6" data-testid="badge-management-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl lg:text-4xl font-bold text-slate-900">Ecusoane QR</h1>
          <p className="text-slate-600 mt-1">Gestioneaza ecusoanele de autentificare ({users.length} utilizatori)</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {selected.size > 0 && (
            <Button
              onClick={handlePrintSelected}
              disabled={printing}
              className="bg-[#0052CC] hover:bg-[#003d99]"
              data-testid="print-selected-btn"
            >
              <Printer className="w-4 h-4 mr-2" />
              {printing ? 'Se pregateste...' : `Printeaza ${selected.size} ecuson${selected.size > 1 ? 'e' : ''}`}
            </Button>
          )}
          <Button onClick={handleBulkGenerate} className="bg-emerald-600 hover:bg-emerald-700" data-testid="bulk-generate-btn">
            <Users className="w-4 h-4 mr-2" /> Genereaza Lipsa
          </Button>
          <Button onClick={handleRegenerateAll} variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" data-testid="regenerate-all-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Regenereaza Toate
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="border-0 shadow-sm"><CardContent className="p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center"><QrCode className="w-5 h-5 text-blue-600" /></div>
          <div><p className="text-2xl font-bold">{withBadge}</p><p className="text-xs text-slate-500">Cu ecuson</p></div>
        </CardContent></Card>
        <Card className="border-0 shadow-sm"><CardContent className="p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center"><CheckCircle className="w-5 h-5 text-emerald-600" /></div>
          <div><p className="text-2xl font-bold">{users.length - withBadge}</p><p className="text-xs text-slate-500">Fara ecuson</p></div>
        </CardContent></Card>
        <Card className="border-0 shadow-sm"><CardContent className="p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center"><Users className="w-5 h-5 text-slate-600" /></div>
          <div><p className="text-2xl font-bold">{users.length}</p><p className="text-xs text-slate-500">Total</p></div>
        </CardContent></Card>
      </div>

      {/* Users Table */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Utilizatori</CardTitle>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Cauta..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-9" data-testid="badge-search" />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0052CC]" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left">
                  <tr>
                    <th className="p-3 w-10">
                      <Checkbox
                        checked={allSelected}
                        onCheckedChange={toggleAll}
                        data-testid="select-all-checkbox"
                      />
                    </th>
                    <th className="p-3 font-medium text-slate-600">Utilizator</th>
                    <th className="p-3 font-medium text-slate-600">Rol</th>
                    <th className="p-3 font-medium text-slate-600">Status Ecuson</th>
                    <th className="p-3 font-medium text-slate-600 text-right">Actiuni</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filtered.map(u => (
                    <tr key={u.id} className={`hover:bg-slate-50 ${selected.has(u.id) ? 'bg-blue-50/50' : ''}`}>
                      <td className="p-3">
                        <Checkbox
                          checked={selected.has(u.id)}
                          onCheckedChange={() => toggleSelect(u.id)}
                          data-testid={`select-user-${u.id}`}
                        />
                      </td>
                      <td className="p-3">
                        <div>
                          <p className="font-medium text-slate-900">{u.full_name}</p>
                          <p className="text-xs text-slate-500">{u.email}</p>
                        </div>
                      </td>
                      <td className="p-3">
                        <Badge variant="outline" className="text-xs">{roleLabels[u.role] || u.role}</Badge>
                      </td>
                      <td className="p-3">
                        {u.has_badge ? (
                          <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                            <CheckCircle className="w-3 h-3 mr-1" /> Activ
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="text-xs">Fara ecuson</Badge>
                        )}
                      </td>
                      <td className="p-3">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => handleGenerate(u.id)}
                            disabled={generating === u.id}
                            title={u.has_badge ? 'Regenereaza ecuson' : 'Genereaza ecuson'}
                            data-testid={`generate-badge-${u.id}`}
                          >
                            {generating === u.id ? (
                              <span className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin inline-block" />
                            ) : (
                              <QrCode className="w-4 h-4 text-blue-600" />
                            )}
                          </Button>
                          {u.has_badge && (
                            <Button variant="ghost" size="sm" onClick={() => handleRevoke(u.id)} title="Revoca ecuson" className="text-red-600" data-testid={`revoke-badge-${u.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BadgeManagementPage;
